import { RefObject, useEffect, useMemo, useRef } from 'react';

export type ThreadGestureEvent = WheelEvent | TouchEvent | MouseEvent | KeyboardEvent;

export interface ThreadGestureClock {
  /** `performance.now()` of the last live gesture on the thread; 0 before the first one. */
  lastGestureAt(): number;
  subscribe(listener: (event: ThreadGestureEvent) => void): () => void;
}

/*
  One set of gesture listeners on the thread scroller, shared by everything that needs
  to tell a live user gesture apart from programmatic or browser-driven scrolling
  (load-more gating, prepend scroll lock, deep-link highlight dismissal).
*/
export function useThreadGestureClock(
  chatRef: RefObject<HTMLDivElement | null>,
): ThreadGestureClock {
  const lastGestureAtRef = useRef(0);
  const listenersRef = useRef(new Set<(event: ThreadGestureEvent) => void>());

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;

    const onGesture = (event: ThreadGestureEvent) => {
      lastGestureAtRef.current = performance.now();
      listenersRef.current.forEach((listener) => listener(event));
    };
    el.addEventListener('wheel', onGesture, { passive: true });
    el.addEventListener('touchmove', onGesture, { passive: true });
    // scrollbar-thumb drags produce no wheel events, only a mousedown on the scroller
    el.addEventListener('mousedown', onGesture);
    el.addEventListener('keydown', onGesture);
    return () => {
      el.removeEventListener('wheel', onGesture);
      el.removeEventListener('touchmove', onGesture);
      el.removeEventListener('mousedown', onGesture);
      el.removeEventListener('keydown', onGesture);
    };
  }, [chatRef]);

  return useMemo(
    () => ({
      lastGestureAt: () => lastGestureAtRef.current,
      subscribe: (listener) => {
        listenersRef.current.add(listener);
        return () => {
          listenersRef.current.delete(listener);
        };
      },
    }),
    [],
  );
}
